import axios from 'axios';
import Helper from './utils/Helper';
import Smartlook from 'smartlook-client'
import { HelmetProvider } from 'react-helmet-async';
import { useLocation } from "react-router-dom";
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from './components/Error/Error';
import { AuthGateProvider } from './components/AuthModal/AuthGate';
import './App.css';
import { WebSiteRoutes } from './WebSiteRoutes/WebSiteRoutes';
import { NavBar } from "./commonComponents";
import { drawsOwnHeader, isLandingPath } from "./utils/siteChrome";
import { Footer } from './commonComponents';
import { DevMockBadge } from './components/DevMockBadge/DevMockBadge';

import Api from './utils/Api/Api';
import { page, identify, hasIdentity, setMarketProperty } from './utils/analyticsService';
import { getMarket, settleMarket, isBlocked, countryName } from './utils/market';
import { NotAvailable } from './components/NotAvailable/NotAvailable';
import { StoreNotice } from './components/StoreNotice/StoreNotice';
import { AppBanner } from './components/AppBanner/AppBanner';
import { syncSmartBanner } from './utils/appBanner';


if (process.env.REACT_APP_ENV === 'production') {
  Smartlook.init('16ede8dcfbea40c36dd752c9a5dec79f5674d402')
}

// Private / transactional routes that must never be indexed.
const NOINDEX_ROUTES = [
  'login', 'signup', 'verify-user', 'account', 'myorder', 'cart',
  'checkout', 'coupon', 'order', 'order-confirmation', 'orderdetail',
  // Per-recipient gift links: private, and one per token.
  'gift',
];

function App() {
  localStorage.setItem('webApp', true);

  const location = useLocation();
  let { pathname } = location;
  pathname = pathname.split('/')[1];
  const noIndex = NOINDEX_ROUTES.includes(pathname);
  // A store's home (/us) is the same landing page as India's /.
  const isLandingRoute = isLandingPath(location.pathname);

  // Keep a robots meta tag in sync with the route. Done imperatively rather
  // than via Helmet: Helmet tags do not reliably reach the document in this
  // app (see utils/useSeo.js), and noindex on private routes must be reliable.
  useEffect(() => {
    const EL_ID = 'robots-noindex';
    const existing = document.getElementById(EL_ID);
    if (noIndex) {
      if (!existing) {
        const meta = document.createElement('meta');
        meta.id = EL_ID;
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
      }
    } else if (existing) {
      existing.remove();
    }
  }, [noIndex]);

  // Exactly one pageview per route change (the wrapper also dedupes).
  useEffect(() => {
    page(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // iOS Smart App Banner: this store's app, this page's URL (utils/appBanner.js).
  useEffect(() => {
    syncSmartBanner();
  }, [location.pathname, location.search]);

  // Settle the country store once: the server switch and Cloudflare's country.
  // Reloads only when the store has to change, so India never reloads.
  useEffect(() => {
    setMarketProperty(getMarket());
    let cancelled = false;
    settleMarket().then((market) => {
      if (!cancelled && market !== getMarket()) window.location.reload();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Signed in from an earlier visit but no stored analytics id (sessions that
  // predate this build): fetch the profile once and identify by user_id.
  useEffect(() => {
    if (!localStorage.getItem('accesstoken') || hasIdentity()) return;
    Api.GetUserDetails()
      .then((res) => identify(res?.data?.user_id))
      .catch(() => {});
  }, []);

  // The sign-in pages draw their own header (utils/siteChrome.js).
  let showNavBar = drawsOwnHeader(location.pathname);
  const isMobile = Helper.isMobile()

  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  const isLoggedIn = localStorage.getItem('accesstoken') ? true : false;
  if (isLoggedIn) {
    axios.defaults.headers.common.Authorization = localStorage.getItem('accesstoken');
  }

  axios.interceptors.response.use((response) => response, (error) => {
    if (error && error.response && error.response.status === 401) {
      localStorage.removeItem('accesstoken');
      window.location.href = '/login';
    }
    return error;
  });

  // A blocked country gets no store at all (not India's either): one page that says so.
  if (isBlocked()) {
    return (
      <div className="App">
        <NotAvailable country={countryName(getMarket())} />
      </div>
    );
  }

  return (
    <HelmetProvider >
      <div className="App">
        <DevMockBadge />
        <StoreNotice />
        <AppBanner />

        <AuthGateProvider>
          {!isMobile && !showNavBar && <NavBar isLoggedIn={isLoggedIn} landing={isLandingRoute} />}
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <WebSiteRoutes isLoggedIn={isLoggedIn} />
          </ErrorBoundary>
          {!isMobile && !showNavBar && <Footer />}
        </AuthGateProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
